import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
const API_BASE_URL = API_URL.replace(/\/api\/?$/, '');
const ASSET_BASE_URL = process.env.REACT_APP_ASSET_URL || API_BASE_URL;

export const buildAssetUrl = (assetPath) => {
  if (!assetPath) return '';
  if (/^(https?:|blob:|data:)/i.test(String(assetPath))) {
    return String(assetPath);
  }
  const base = ASSET_BASE_URL.replace(/\/$/, '');
  const path = String(assetPath).replace(/^\//, '');
  return `${base}/${path}`;
};

const api = axios.create({
  baseURL: API_URL,
  timeout: 240000, // 4 minutes
});

const withParams = ({ signal, ...params } = {}) => ({ params, signal });

// Add token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Auth API
export const authAPI = {
  register: (username, password) => api.post('/auth/register', { username, password }),
  login: (username, password) => api.post('/auth/login', { username, password }),
};

// Projects API
export const projectsAPI = {
  getAll: (config) => api.get('/projects', config),
  create: (name, parentId) => api.post('/projects', { name, parentId }),
  update: (id, name) => api.put(`/projects/${id}`, { name }),
  delete: (id) => api.delete(`/projects/${id}`),
  updateSettings: (id, settings) => api.put(`/projects/${id}/settings`, settings),
  getReferences: (id, config) => api.get(`/projects/${id}/references`, config),
  addReferenceFromGallery: (id, payload) => api.post(`/projects/${id}/references/use`, payload),
  uploadReference: (id, formData) => api.post(`/projects/${id}/references`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  deleteReference: (projectId, referenceId) => api.delete(`/projects/${projectId}/references/${referenceId}`),
};

// Generations API
export const profileAPI = { getSummary: (config) => api.get('/profile', config) };

export const generationsAPI = {
  getStorage: () => api.get('/generations/storage'),
  getAll: (params) => api.get('/generations', withParams(params)),
  getLibrary: (params) => api.get('/generations/library', withParams(params)),
  getMy: (params) => api.get('/generations/my', withParams(params)),
  getByProject: (projectId, params) => api.get(`/generations/project/${projectId}`, withParams(params)),
  getFailed: (params) => api.get('/generations/failed', withParams(params)),
  create: (formData) => api.post('/generations', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  // Create with SSE for retry status updates
  createWithStream: (formData, callbacks) => {
    const token = localStorage.getItem('token');
    const { onRetry, onComplete, onError } = callbacks;

    return fetch(`${API_URL}/generations/stream`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
      body: formData,
    }).then(async (response) => {
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        onError?.({ success: false, error: data.error || 'La génération a échoué.', statusCode: response.status });
        return;
      }
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let terminal = false;
      const dispatch = (block) => {
        const lines = block.split('\n');
        const event = lines.find(line => line.startsWith('event:'))?.slice(6).trim();
        const payload = lines.filter(line => line.startsWith('data:')).map(line => line.slice(5).trimStart()).join('\n');
        if (!payload) return;
        let data;
        try { data = JSON.parse(payload); } catch (_) { return; }
        if (event === 'retry') onRetry?.(data);
        if (event === 'complete') { terminal = true; onComplete?.(data); }
        if (event === 'error') { terminal = true; onError?.(data); }
      };
      try {
        while (true) {
          const { done, value } = await reader.read();
          buffer += decoder.decode(value, { stream: !done });
          let boundary;
          while ((boundary = buffer.indexOf('\n\n')) !== -1) {
            dispatch(buffer.slice(0, boundary));
            buffer = buffer.slice(boundary + 2);
          }
          if (done) break;
        }
        if (buffer.trim()) dispatch(buffer);
        if (!terminal) onError?.({ success: false, error: 'La connexion a été interrompue. Vérifiez votre historique avant de réessayer.' });
      } finally { reader.releaseLock(); }
    });
  },
  delete: (id) => api.delete(`/generations/${id}`),
  toggleLibrary: (id, inLibrary) => api.patch(`/generations/${id}/library`, { inLibrary }),
};

// Prompt templates API
export const promptTemplatesAPI = {
  getAll: () => api.get('/prompt-templates'),
  create: (text) => api.post('/prompt-templates', { text }),
  delete: (id) => api.delete(`/prompt-templates/${id}`),
};

// Users API
export const usersAPI = {
  heartbeat: () => api.post('/users/heartbeat'),
  getOnline: () => api.get('/users/online'),
};

// Downscaler API
export const downscalerAPI = {
  process: (formData) => api.post('/downscaler/process', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  getAll: (params) => api.get('/downscaler', withParams(params)),
  delete: (id) => api.delete(`/downscaler/${id}`),
};

export default api;
